import { useCallback } from 'react'
import { type To, useLocation, useNavigate } from 'react-router-dom'

import { parseQuery } from '@divvy-web/utils.routing'

import { PATHNAME_CONSTS } from '@sputnik/resources/constants'
import { setReturnFlowLocalStorage } from '@sputnik/utils/neoRedirectUtils'

export const useAuthNavigation = () => {
  const navigate = useNavigate()
  const { search } = useLocation()
  const searchParams = search ?? ''

  const goToDashboard = useCallback(
    (clearSearchParams: boolean = false, referrerSearch: string = '') => {
      const returnFlowParam = parseQuery(referrerSearch)?.returnFlow
      returnFlowParam && setReturnFlowLocalStorage(returnFlowParam)

      navigate(
        {
          pathname: PATHNAME_CONSTS.DASHBOARD_PATH,
          search: clearSearchParams ? '' : searchParams,
        },
        {
          replace: referrerSearch?.includes('redirect_to'),
          state: {
            referrer: PATHNAME_CONSTS.AUTH_PATH,
            referrerSearch,
          },
        },
      )
    },
    [navigate, searchParams],
  )

  const goToAuth = useCallback(
    (clearSearchParams: boolean = false) =>
      navigate(
        {
          pathname: PATHNAME_CONSTS.AUTH_PATH,
          search: clearSearchParams ? '' : searchParams,
        },
        { state: { logout: 'self' } },
      ),
    [navigate, searchParams],
  )

  const goToVerifyCode = useCallback(
    (referrer: string | null = null, clearSearchParams: boolean = false) => {
      navigate(
        { pathname: PATHNAME_CONSTS.VERIFY_EMAIL_PATH, search: clearSearchParams ? '' : searchParams },
        { state: { referrer } },
      )
    },
    [navigate, searchParams],
  )

  const goToSignup = useCallback(
    (clearSearchParams: boolean = false) => {
      navigate(
        {
          pathname: PATHNAME_CONSTS.GET_TO_KNOW_YOU,
          search: clearSearchParams ? '' : searchParams,
        },
        { state: { referrer: PATHNAME_CONSTS.VERIFY_CODE_PATH } },
      )
    },
    [navigate, searchParams],
  )

  const goToRightFitSurveyPage = useCallback(
    (clearSearchParams: boolean = false) => {
      navigate(
        {
          pathname: PATHNAME_CONSTS.GOOD_FIT_SURVEY_PATH,
          search: clearSearchParams ? '' : searchParams,
        },
        { replace: true },
      )
    },
    [navigate, searchParams],
  )

  const goToReferrer = useCallback(
    (referrer: To | null = null) => {
      if (referrer) {
        navigate(referrer)
      }
    },
    [navigate],
  )

  const setReferrer = useCallback(
    (referrer: string) => navigate({}, { replace: true, state: { referrer } }),
    [navigate],
  )

  return {
    goToAuth,
    goToDashboard,
    goToRightFitSurveyPage,
    goToReferrer,
    goToSignup,
    goToVerifyCode,
    setReferrer,
  }
}
